import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import { formatBookingDate } from '../utils/dateFormat';
import axios from 'axios';

function BookingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [existingBookings, setExistingBookings] = useState([]);
  const [lunacalSettings, setLunacalSettings] = useState(null);
  const [coreCompleted, setCoreCompleted] = useState(false);
  const lunacalRef = useRef(null);
  const embedLoaded = useRef(false);
  
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchData();
      // Check CORE completion
      axios.get('/api/assessments').then(res => {
        const core = (res.data.assessments || []).find(a => a.type === 'archetype' && a.status === 'completed');
        setCoreCompleted(!!core);
        if (!core && user.role !== 'admin') {
          navigate('/dashboard');
        }
      }).catch(() => {});
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (lunacalSettings && user && !embedLoaded.current) {
      loadLunacalEmbed();
    }
  }, [lunacalSettings, user]);
  
  const fetchData = async () => {
    try {
      const [bookingsRes, settingsRes] = await Promise.all([
        axios.get('/api/bookings'),
        axios.get('/api/integrations/lunacal')
      ]);
      setExistingBookings(bookingsRes.data.bookings || []);
      setLunacalSettings(settingsRes.data.lunacal);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setLunacalSettings({
        enabled: true,
        calLink: 'team/secretsauce/dicover-path',
        embedType: 'inline',
        forwardQueryParams: true
      });
    } finally {
      setLoading(false);
    }
  };
  
  const loadLunacalEmbed = useCallback(() => {
    if (embedLoaded.current || !lunacalSettings || !lunacalRef.current) return;
    embedLoaded.current = true;
    
    const calLink = lunacalSettings.calLink || 'team/secretsauce/dicover-path';
    const namespace = calLink.split('/').pop() || 'dicover-path';
    
    // Clear container
    lunacalRef.current.innerHTML = '';
    
    // Create the inline target div
    const targetDiv = document.createElement('div');
    targetDiv.style.cssText = 'width:100%;height:100%;overflow:scroll';
    targetDiv.id = `my-lunacal-inline-${namespace}`;
    lunacalRef.current.appendChild(targetDiv);
    
    // Load Lunacal embed script and initialise
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.textContent = `
      (function(L,U,N){let p=(a,ar)=>a.q.push(ar),d=L.document;L.Lunacal=L.Lunacal||function(){let lun=L.Lunacal,ar=arguments;if(!lun.loaded){lun.ns={};lun.q=lun.q||[];d.head.appendChild(d.createElement("script")).src=U;lun.loaded=!0}if(ar[0]===N){const api=function(){p(api,arguments)};const ns=ar[1];api.q=api.q||[];if(typeof ns==="string"){lun.ns[ns]=lun.ns[ns]||api;p(lun.ns[ns],ar);p(lun,["initNamespace",ns])}else p(lun,ar);return}p(lun,ar)};if(!L.Cal)L.Cal=L.Lunacal})(window,"https://app.lunacal.ai/embed/embed.js","init");
      Lunacal("init","${namespace}",{origin:"https://app.lunacal.ai"});
      Lunacal.config = Lunacal.config || {};
      Lunacal.config.forwardQueryParams = ${lunacalSettings.forwardQueryParams !== false};
      Lunacal.ns["${namespace}"]("inline", {
        elementOrSelector:"#my-lunacal-inline-${namespace}",
        config: {"layout":""},
        calLink: "${calLink}",
      });
      Lunacal.ns["${namespace}"]("ui", {"theme":"dark","styles":{"branding":{}},"hideEventTypeDetails":false,"layout":"","cssVarsPerTheme":{"light":{"theme-border":"#E7E7EE","theme-background-primary":"#7033FF","theme-background-secondary":"#E2EBFF","theme-background-card":"#FFFFFF","theme-background-base":"#FFFFFF","theme-text-primary":"#FFFFFF","theme-text-secondary":"#1E69DC","theme-text-card":"#000000","theme-text-base":"#0B0D0E","theme-rounded-base":"20px","theme-rounded-calendar":" 24px","theme-rounded-timeslot":"100px","theme-rounded-day":"100px","theme-rounded-button":"100px","theme-shadow-calendar":"2px 2px 4px 0px #0000001A","theme-shadow-button":"1px 2px 2px 0px #0000001A","theme-shadow-timeslot":"0px 0px 0px 0px #000000","theme-font-family":"Inter"},"dark":{"theme-border":"#33353A","theme-background-primary":"#7033FF","theme-background-secondary":"#1E293B","theme-background-card":"#222327","theme-background-base":"#1A1B1E","theme-text-primary":"#FFFFFF","theme-text-secondary":"#1E69DC","theme-text-card":"#FFFFFF","theme-text-base":"#FFFFFF","theme-rounded-base":"20px","theme-rounded-calendar":" 24px","theme-rounded-timeslot":"100px","theme-rounded-day":"100px","theme-rounded-button":"100px","theme-shadow-calendar":"2px 2px 4px 0px #0000001A","theme-shadow-button":"1px 2px 2px 0px #0000001A","theme-shadow-timeslot":"0px 0px 0px 0px #000000","theme-font-family":"Inter"}},"displayedContent":{"image":true,"name":true,"description":true,"eventName":true,"highlightBar":true},"background":null,"stylePreset":null});
    `;
    document.body.appendChild(script);
  }, [lunacalSettings, user]);
  
  const getUpcomingBookings = () => {
    const now = new Date();
    return existingBookings.filter(b => 
      b.status === 'confirmed' && new Date(b.scheduledAt) > now
    );
  };
  
  const getPastBookings = () => {
    const now = new Date();
    return existingBookings.filter(b => 
      new Date(b.scheduledAt) <= now || b.status === 'completed'
    );
  };
  
  const formatDate = (dateStr) => {
    return formatBookingDate(dateStr);
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      completed: 'bg-blue-100 text-blue-700 border-blue-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      pending_approval: 'bg-amber-100 text-amber-700 border-amber-200'
    };
    
    const labels = {
      pending_approval: 'Pending Approval',
      rejected: 'Rejected'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {labels[status] || (status.charAt(0).toUpperCase() + status.slice(1))}
      </span>
    );
  };
  
  if (authLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  
  const upcomingBookings = getUpcomingBookings();
  
  return (
    <div className="max-w-4xl" data-testid="booking-page">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Book a Session</h1>
        <p className="text-sm text-muted-foreground mt-1">Schedule your facilitated assessment session</p>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        {/* Introduction */}
        <div className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1">
          <h2 className="h3 text-xl mb-4">Facilitated Session</h2>
          <p className="body text-muted-foreground mb-6">
            Book a one-on-one session to discuss your diagnostic results in depth. 
            Together, we will explore your pressure patterns, leadership profile, and create 
            an actionable plan for your leadership development.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center gap-3 text-sm">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-muted-foreground">30 minutes</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="text-muted-foreground">Video call</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-muted-foreground">Personalized insights</span>
              </div>
            </div>
            
            {user && (
              <div className="bg-muted/30 rounded-[var(--radius)] p-4 text-sm">
                <p className="text-muted-foreground">
                  Booking as: <strong className="text-foreground">{user.firstName} {user.lastName}</strong> ({user.email})
                </p>
              </div>
            )}
          </div>
          
          {/* Upcoming Bookings */}
          {upcomingBookings.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-[var(--radius)] p-6">
              <h3 className="font-semibold mb-4 text-emerald-800 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Your Upcoming Sessions
              </h3>
              <div className="space-y-3">
                {upcomingBookings.map((booking) => (
                  <div 
                    key={booking.id} 
                    className="bg-white rounded-lg p-4 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-emerald-900">{formatDate(booking.scheduledAt)}</p>
                      <p className="text-sm text-emerald-600">Facilitated Session</p>
                    </div>
                    {getStatusBadge(booking.status)}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Lunacal Booking Widget */}
          <div className="bg-card border border-border rounded-[var(--radius)] overflow-hidden elev-1">
            <div className="p-4 border-b border-border bg-muted/30">
              <h3 className="font-semibold">Select a Time</h3>
              <p className="text-sm text-muted-foreground mt-1">Choose a convenient slot for your session</p>
            </div>
            <div 
              ref={lunacalRef} 
              className="lunacal-container"
              data-testid="lunacal-embed"
              style={{ minHeight: '650px' }}
            >
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <svg className="animate-spin h-8 w-8 mr-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading calendar...
              </div>
            </div>
          </div>
          
          {/* What to Expect */}
          <div className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
            <h3 className="font-semibold mb-4">What to Expect</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">1</span>
                <div>
                  <h4 className="font-medium mb-1">Confirmation Email</h4>
                  <p className="text-sm text-muted-foreground">You will receive an email with your booking details and calendar invite.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">2</span>
                <div>
                  <h4 className="font-medium mb-1">24-Hour Reminder</h4>
                  <p className="text-sm text-muted-foreground">We will send you a reminder email the day before your session.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">3</span>
                <div>
                  <h4 className="font-medium mb-1">Your Session</h4>
                  <p className="text-sm text-muted-foreground">Join the video call at your scheduled time. Come prepared with your questions!</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Past Bookings */}
          {getPastBookings().length > 0 && (
            <div className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1">
              <h3 className="font-semibold mb-4">Past Sessions</h3>
              <div className="space-y-3">
                {getPastBookings().map((booking) => (
                  <div 
                    key={booking.id} 
                    className="flex items-center justify-between py-3 border-b border-border last:border-0"
                  >
                    <div>
                      <p className="font-medium">{formatDate(booking.scheduledAt)}</p>
                      <p className="text-sm text-muted-foreground">Facilitated Session</p>
                    </div>
                    {getStatusBadge(booking.status)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
    </div>
  );
}

export default BookingPage;
